import { Map } from "immutable";
import { camelCase } from "lodash";
import { plural } from "pluralize";
import { createSelector } from "reselect";
import i18n from "~/i18n";
import { descSortCompare } from "~/utils/utils.jsx";
import { createImmutableIsEqualSelector } from "./index.jsx";
import { getObjectHasNewComments } from "./selector.additionalObjectData.jsx";
import { getNormalAttachments } from "./selector.attachments.jsx";
import { getLastVisit, getParentLastVisitTimestampSelector } from "./selector.lastVisit.jsx";
import { getAllUsers, getLoggedUserId } from "./selector.users.jsx";

export const getCommentById = (state, id) => state.comments.get(id.toString());
export const getAllComments = (state) => state.comments;

const getCommentsByParent = (state, parentType, parentId) => {
  return getAllComments(state).filter((c) => {
    return c.parent_type === parentType && c.parent_id === parentId;
  });
};

const getCommentCreatedBySelector = createImmutableIsEqualSelector([getCommentById, getAllUsers], (comment, users) =>
  users.get(comment.created_by_id.toString())
);

const getCommentAttachmentsSelector = createImmutableIsEqualSelector(
  [getCommentById, getNormalAttachments],
  (comment, attachments) =>
    attachments.filter((a) => {
      return comment.attachments.indexOf(a.id) > -1;
    })
);

const getCommentDropAttachedFileIdsList = (state, id) => {
  return getCommentById(state, id).drop_attached_files;
};

const getCommentIdsByParentSortedSelector = createImmutableIsEqualSelector([getCommentsByParent], (comments) =>
  comments
    .sort((a, b) => descSortCompare(a.created_on, b.created_on))
    .filter((c) => !c.is_trashed)
    .map((c) => c.id)
);

export const makeCommentCreatedBySelector = () => {
  return createImmutableIsEqualSelector(getCommentCreatedBySelector, (createdBy) => createdBy);
};

export const makeCommentAttachmentsSelector = () => {
  return createImmutableIsEqualSelector(
    getCommentAttachmentsSelector,
    getCommentDropAttachedFileIdsList,
    (commentAttachments, listOfDroppedFileIds) => {
      return commentAttachments.filter((attachment) => {
        return listOfDroppedFileIds.indexOf(attachment.id) < 0;
      });
    }
  );
};

export const makeCommentIdsByParentSortedSelector = () => {
  return createImmutableIsEqualSelector(getCommentIdsByParentSortedSelector, (commentIds) => commentIds.toList());
};

/**
 * @deprecated
 * @returns {*}
 */
export const makeIsCommentNewSelector = () => {
  return createSelector(getLastVisit, getCommentById, (lastVisit, comment) => {
    if (comment) {
      const lastVisitTimestamp = lastVisit[`${comment.parent_type}-${comment.parent_id}`]?.last_visit_timestamp;
      return typeof lastVisitTimestamp === `number` && lastVisitTimestamp < comment.created_on;
    }
    return false;
  });
};

const getCommentParentType = createSelector(getCommentById, (comment = Map()) => comment.get(`parent_type`));

const getCommentParentId = createSelector(getCommentById, (comment = Map()) => comment.get(`parent_id`));

const makeAllCommentsByParentSeletor = () => {
  return createSelector(getAllComments, getCommentParentType, getCommentParentId, (comments, parentType, parentId) =>
    comments.filter((comment) => {
      return comment.get(`parent_id`) === parentId && comment.get(`parent_type`) === parentType;
    })
  );
};

const getParentCommentsCount = (state, id) => {
  const comment = getCommentById(state, id);

  if (comment) {
    const type = camelCase(plural(comment.get(`parent_type`)));
    const parentId = comment.get(`parent_id`);
    let collection = state[type] || Map();
    if (Map.isMap(collection)) {
      return collection.get(`${parentId}`, Map()).get("comments_count");
    } else if (collection[parentId]) {
      return collection[parentId].comments_count;
    }
  }

  return 0;
};

const makeCommentParentLastVisitTimestampSelector = () => {
  return createSelector(
    (state) => state,
    getCommentParentType,
    getCommentParentId,
    getParentLastVisitTimestampSelector
  );
};

export const makeNewCommentsTextByCommentSelector = () => {
  const getAllCommentsByParent = makeAllCommentsByParentSeletor();
  const getCommentParentLastVisitTimestamp = makeCommentParentLastVisitTimestampSelector();

  return createSelector(
    getAllCommentsByParent,
    getCommentParentLastVisitTimestamp,
    (comments, last_visit_timestamp) => {
      const new_comments = comments.filter((c) => c.created_on > last_visit_timestamp);

      if (new_comments) {
        return new_comments.size > 1 ? i18n.t("New Comments") : i18n.t("New Comment");
      }
      return ``;
    }
  );
};

export const makeGetIsCommentIdFirstAfterParentLastVisitSelector = () => {
  const getAllCommentsByParent = makeAllCommentsByParentSeletor();
  const getCommentParentLastVisitTimestamp = makeCommentParentLastVisitTimestampSelector();
  return createSelector(
    getAllCommentsByParent,
    getCommentParentLastVisitTimestamp,
    getParentCommentsCount,
    getLoggedUserId,
    (state, id) => id,
    (comments, last_visit_timestamp, count, logged_user_id, id) => {
      let new_comment, old_comment;

      if (typeof last_visit_timestamp !== `number`) {
        return false;
      }

      const sorted_comments = comments.sortBy((comment) => comment.get(`created_on`));

      old_comment = sorted_comments
        .filter((comment) => {
          return comment.get(`created_on`) < last_visit_timestamp;
        })
        .last();

      new_comment = sorted_comments
        .filter((comment) => {
          return comment.get(`created_on`) > last_visit_timestamp && comment.get("created_by_id") !== logged_user_id;
        })
        .first();

      if (sorted_comments.size < count && !old_comment) {
        new_comment = undefined;
      }

      return new_comment ? new_comment.get(`id`) === id : false;
    }
  );
};

export const getNewCommentsCountByParent = createSelector(
  getCommentsByParent,
  getParentLastVisitTimestampSelector,
  getObjectHasNewComments,
  (comments, last_visit_timestamp, visible) => {
    if (visible) {
      const new_comments = comments.filter((comment) => comment.get(`created_on`) > last_visit_timestamp);

      return new_comments.size;
    }
    return 0;
  }
);

export const makeParentHasNewCommentsSelector = () => {
  return createSelector(getCommentsByParent, getParentLastVisitTimestampSelector, (comments, last_visit_timestamp) => {
    if (!last_visit_timestamp) {
      return false;
    }

    const new_comments = comments.filter((comment) => comment.get(`created_on`) > last_visit_timestamp);

    return new_comments.size > 0;
  });
};

export const getLatestCommentSelector = createSelector(getCommentsByParent, (comments) => {
  const sorted = comments
    .sort((a, b) => {
      return descSortCompare(a.created_on, b.created_on);
    })
    .filter((c) => !c.is_trashed);
  const first = sorted.keySeq().first();
  const comment = sorted.get(first);
  return comment;
});

const getParent = (state, parentType) => {
  const reducer = camelCase(plural(parentType));
  return state[reducer];
};

export const getParentOfComment = createSelector(
  (state, parentType, parentId) => parentId,
  getParent,
  (parentId, collection) => {
    if (Map.isMap(collection)) {
      return collection.get(`${parentId}`);
    }
    if (typeof collection === "object") {
      return collection[`${parentId}`];
    }
    return undefined;
  }
);
